import { useState } from "react";
import { Link } from "react-router-dom";
import { articleList } from "@/mocks/articles";
import { mainSiteUrl } from "@/mocks/categories";

const features = [
  {
    title: "Product Research",
    description: "Streamline your product discovery with organized tracking sheets that help you compare prices, quality, and supplier reliability across multiple platforms.",
    icon: "ri-search-eye-line",
  },
  {
    title: "Price Tracking",
    description: "Monitor price fluctuations over time with automated calculation columns. Never overpay again with built-in currency conversion and fee estimation.",
    icon: "ri-funds-line",
  },
  {
    title: "Warehouse Organization",
    description: "Track every item from purchase to warehouse arrival. Know exactly what is in storage, how long it has been there, and when consolidation is possible.",
    icon: "ri-store-3-line",
  },
  {
    title: "Shipping Management",
    description: "Calculate shipping costs across multiple carriers, compare delivery times, and optimize package consolidation for the lowest possible logistics spend.",
    icon: "ri-ship-2-line",
  },
  {
    title: "Shopping Optimization",
    description: "Make data-driven purchasing decisions with complete visibility into your order pipeline, from initial browsing to final doorstep delivery.",
    icon: "ri-shopping-bag-3-line",
  },
  {
    title: "Quality Control",
    description: "Record QC photo statuses, inspection notes, and return decisions in one place. Ensure every item meets your standards before international shipping.",
    icon: "ri-checkbox-multiple-line",
  },
];

const faqItems = [
  {
    question: "What is BaseTao Spreadsheet?",
    answer: "BaseTao Spreadsheet is a structured tracking tool used by international shoppers and e-commerce sellers to organize product research, track orders, calculate shipping costs, and manage warehouse inventory when sourcing goods through Chinese platforms and agent services.",
  },
  {
    question: "Is BaseTao Spreadsheet free to use?",
    answer: "Yes, BaseTao Spreadsheet templates are shared within the community and work with free spreadsheet software like Google Sheets or Microsoft Excel Online. There is no subscription fee for using the template.",
  },
  {
    question: "How does BaseTao Spreadsheet help with shipping?",
    answer: "The spreadsheet includes shipping cost calculators that factor in weight, dimensions, destination country, and carrier options. Users can compare different shipping routes and optimize package consolidation to reduce costs by 15-25%.",
  },
  {
    question: "Can beginners use BaseTao Spreadsheet?",
    answer: "Absolutely. The template is designed with beginners in mind and includes clear column labels, pre-built formulas, and a straightforward workflow. Most users can set up their first tracking sheet in under 30 minutes.",
  },
  {
    question: "Is BaseTao Spreadsheet secure for tracking orders?",
    answer: "The spreadsheet template itself does not collect or transmit any data. Your order information stays on your local device or cloud storage account. We always recommend enabling two-factor authentication on your cloud storage for additional security.",
  },
  {
    question: "How does BaseTao Spreadsheet compare to alternatives?",
    answer: "BaseTao Spreadsheet is purpose-built for the Chinese agent shopping workflow, unlike generic tools that require extensive customization. It includes pre-configured columns for agent fees, domestic Chinese shipping, QC tracking, and consolidation — features that general spreadsheet templates lack.",
  },
];

export default function ContentSections() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      {/* What Is Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6 text-center">
              What Is BaseTao Spreadsheet?
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                BaseTao Spreadsheet is a purpose-built tracking and management tool designed specifically for international shoppers who source products through Chinese e-commerce platforms and agent services. It serves as a centralized command center where you can organize product links, calculate total costs including all fees, monitor warehouse arrivals, track quality control inspections, and plan international shipping — all within a single, customizable spreadsheet.
              </p>
              <p>
                The spreadsheet template is optimized for the unique challenges of cross-border shopping: currency conversion between Chinese Yuan and your local currency, multiple fee layers (agent service fees, domestic shipping, international freight), and complex logistics timelines that span weeks. By consolidating all this information into one organized view, BaseTao Spreadsheet eliminates the chaos of managing orders across scattered notes, browser bookmarks, and mental calculations.
              </p>
              <p>
                Whether you are a casual shopper ordering a handful of items each month or a professional reseller managing hundreds of SKUs, the spreadsheet adapts to your workflow. Beginners appreciate the ready-to-use template that requires no technical setup, while advanced users value the flexibility to add custom columns, formulas, and automation that match their specific business needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why People Use Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4 text-center">
            Why People Use BaseTao Spreadsheet
          </h2>
          <p className="text-sm text-foreground-600 text-center max-w-xl mx-auto mb-12">
            Thousands of international shoppers trust BaseTao Spreadsheet to keep their sourcing organized and efficient
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="p-5 md:p-6 bg-background-50 border border-background-200/70 rounded-xl hover:border-accent-300 transition-colors group"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600 mb-4 group-hover:bg-accent-100 transition-colors">
                  <i className={`${feature.icon} text-lg`}></i>
                </span>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guides Section */}
      <section id="guides" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4 text-center">
            Latest BaseTao Spreadsheet Guides
          </h2>
          <p className="text-sm text-foreground-600 text-center max-w-xl mx-auto mb-12">
            In-depth tutorials, reviews, and expert tips to help you get the most out of BaseTao Spreadsheet
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
            {articleList.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2.5 py-0.5 bg-accent-50 text-accent-700 text-xs font-medium rounded-full">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-500">{article.readTime}</span>
                  </div>
                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-1 mt-4 text-xs font-medium text-primary-600">
                    Read Article
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4 text-center">
            Frequently Asked Questions
          </h2>
          <p className="text-sm text-foreground-600 text-center mb-12">
            Everything you need to know about BaseTao Spreadsheet
          </p>

          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {faqItems.map((item, idx) => (
              <div
                key={idx}
                className="border border-background-200/70 rounded-xl overflow-hidden"
                itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-100 transition-colors"
                >
                  <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4" itemProp="name">
                    {item.question}
                  </h3>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`text-lg text-foreground-500 transition-transform duration-200 ${
                        openFaq === idx ? "ri-subtract-line" : "ri-add-line"
                      }`}
                    ></i>
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="text-sm text-foreground-600 leading-relaxed" itemProp="text">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">
            Ready to Use BaseTao Spreadsheet Efficiently?
          </h2>
          <p className="text-sm text-foreground-600 max-w-lg mx-auto mb-8">
            Start organizing your product research, tracking orders, and optimizing your international shopping workflow today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              Start Learning Now
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3 border border-background-300 text-foreground-700 font-medium rounded-lg hover:border-background-400 hover:text-foreground-900 transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              Visit BaseTao Spreadsheet
              <i className="ri-external-link-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}